import { createApp } from 'vue'
import './style.css'
import './crafter-chatroom-markdown.css'
import 'flag-icons/css/flag-icons.min.css'
import App from './App.vue'
import router from './router'
import i18n from './i18n'
import { clerkPlugin } from '@clerk/vue';
import { itIT, enUS } from '@clerk/localizations';
import { getInitialLocale } from './utils/locale';
import { createPinia } from 'pinia';
import RollbarPlugin from './plugins/rollbar';
import UmamiPlugin from './plugins/umami';
import PrimeVueFocusTrapGuard from './plugins/primevueFocusTrapGuard';
import PrimeVueLocaleSync from './plugins/primevueLocale';
import { parseAllowedRedirectOrigins, isExternalUrl } from './utils/redirectOrigins';

// Dopo un deploy gli asset con hash vecchio non esistono più sull'origin e il
// catchall risponde index.html (text/html) al posto del chunk JS: l'import
// dinamico fallisce e la route resta bianca. Vite emette `vite:preloadError`:
// si ricarica la pagina per prendere l'index.html nuovo. Il flag in
// sessionStorage evita loop di reload se anche l'HTML è stantio in cache.
window.addEventListener('vite:preloadError', (event) => {
  const KEY = 'vite-preload-error-reloaded';
  if (sessionStorage.getItem(KEY)) return; // già ritentato: lascia emergere l'errore
  sessionStorage.setItem(KEY, '1');
  event.preventDefault();
  window.location.reload();
});

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY;
if (!CLERK_PUBLISHABLE_KEY) {
  throw new Error('Missing VITE_CLERK_PUBLISHABLE_KEY in environment variables');
}

import PrimeVue from 'primevue/config';
import Tooltip from 'primevue/tooltip';
import ToastService from 'primevue/toastservice';
import ConfirmationService from 'primevue/confirmationservice';
import Crafter from './presets/crafter';

const app = createApp(App)

app.use(createPinia());

const initialLocale = getInitialLocale();

// Override della label di consenso legale del sign-up.
// Chiave Clerk (annidata): signUp.legalConsent.checkbox.label__termsOfServiceAndPrivacyPolicy.
// Nel template `{{ termsOfServiceLink || link("…") }}` il token rende un link verso l'URL
// passato via appearance.options.termsPageUrl/privacyPageUrl (vedi SignUpView). "DPA" è testo
// adiacente al link dei Termini (stesso documento dei Termini di Servizio).
const consentLabel = {
  it: 'Accetto i {{ termsOfServiceLink || link("Termini del Servizio") }}, DPA e l\'{{ privacyPolicyLink || link("Informativa sulla Privacy") }}',
  en: 'I accept the {{ termsOfServiceLink || link("Terms of Service") }}, DPA and the {{ privacyPolicyLink || link("Privacy Policy") }}',
};

const baseLocalization = initialLocale === 'it' ? itIT : enUS;

// Deep-merge solo della foglia label__… per non sovrascrivere il resto del blocco signUp.
const clerkLocalization = {
  ...baseLocalization,
  signUp: {
    ...baseLocalization.signUp,
    legalConsent: {
      ...baseLocalization.signUp?.legalConsent,
      checkbox: {
        ...baseLocalization.signUp?.legalConsent?.checkbox,
        label__termsOfServiceAndPrivacyPolicy: consentLabel[initialLocale] || consentLabel.en,
      },
    },
  },
};

// Origini esterne verso cui Clerk può fare redirect dopo il login (es. ritorno all'app
// Handover che apre il login con `/sign-in?redirect_url=<url handover>`). Senza whitelist
// Clerk considera il redirect_url cross-origin "unsafe" e ricade sul fallback (/app/bots).
const ALLOWED_REDIRECT_ORIGINS = parseAllowedRedirectOrigins(
  import.meta.env.VITE_ALLOWED_REDIRECT_ORIGINS,
);

// Clerk delega la navigazione a vue-router; ma per i redirect cross-origin (l'app Handover
// vive su un'altra origine) vue-router non può navigare. In quei casi Clerk passa
// `metadata.windowNavigate`: lo usiamo per la hard navigation (con fallback su
// window.location). Senza questo il redirect verso l'altra origine resterebbe appeso nella SPA.
const routerPush = (to, metadata) => {
  if (isExternalUrl(to, window.location.origin)) {
    return (metadata?.windowNavigate ?? ((url) => window.location.assign(url)))(to);
  }
  return router.push(to);
};
const routerReplace = (to, metadata) => {
  if (isExternalUrl(to, window.location.origin)) {
    return (metadata?.windowNavigate ?? ((url) => window.location.replace(url)))(to);
  }
  return router.replace(to);
};

app.use(clerkPlugin, {
  publishableKey: CLERK_PUBLISHABLE_KEY,
  localization: clerkLocalization,
  // Path interni delle nostre pagine SPA (no hash: la navigazione passa da vue-router sotto).
  signInUrl: '/sign-in',
  signUpUrl: '/sign-up',
  signInFallbackRedirectUrl: '/app/bots',
  signUpFallbackRedirectUrl: '/app/bots',
  // Whitelist delle origini esterne consentite per `redirect_url` (vedi sopra).
  allowedRedirectOrigins: ALLOWED_REDIRECT_ORIGINS,
  // Deleghiamo la navigazione di Clerk a vue-router (redirect post-auth, cross-link
  // sign-in/up), con hard navigation per i redirect cross-origin.
  routerPush,
  routerReplace,
});

app.use(router)
app.use(i18n)

app.directive('tooltip', Tooltip);

app.use(PrimeVue, {
  theme: {
    preset: Crafter,
    options: {
      darkModeSelector: '.dark-mode',
      cssLayer: false
    }
  }
});

// PrimeVue's own strings (DatePicker names and date format, Password prompt, aria
// labels…) follow the vue-i18n locale (see plugins/primevueLocale.js).
app.use(PrimeVueLocaleSync, { i18n });

// Guards PrimeVue's FocusTrap against a null `nextSibling` crash. Must be installed
// before the first Dialog/Popover mounts (see plugins/primevueFocusTrapGuard.js).
app.use(PrimeVueFocusTrapGuard);

app.use(ToastService);
app.use(ConfirmationService);

app.use(RollbarPlugin);
app.use(UmamiPlugin);

app.mount('#app')

// App montata: l'eventuale retry post-deploy è andato a buon fine, si riarma.
sessionStorage.removeItem('vite-preload-error-reloaded');

// Rimuove il param cache-busting `cb` aggiunto dal recovery in index.html.
if (new URLSearchParams(window.location.search).has('cb')) {
  const url = new URL(window.location.href);
  url.searchParams.delete('cb');
  window.history.replaceState(window.history.state, '', url.toString());
}
